import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPreview } from '@lib/sanity/client'
import { getAllLocales, Locale } from '@lib/language'
import { CustomStaticPropsContext, StaticPathWithId } from '@lib/routes'
import { getPrintShopPage, getAllPrintShopIds } from '@lib/sanity/shop'
import {
  SanityPrintShopPage,
  SanityPrintShopType,
} from '@data/sanity/queries/types/shop'
import { useSetPrintShop } from '@lib/shop'

import Modules from '@modules/modules'
import Layout from '@components/layout'
import ShopTemplateHeader from '@modules/shop/template/header'
import Collection from '@modules/shop/collection/index'

interface YearShopPageProps {
  locale: Locale
  page: SanityPrintShopPage
  site: SanitySiteFragment
}

const YearShopPage = ({ page, site }: YearShopPageProps) => {
  useSetPrintShop({
    id: page.id,
    name: page.name,
    active: page.active,
    type: page.type,
  })

  return (
    <Layout page={page.template} site={site}>
      <ShopTemplateHeader
        title={page.template.title
          .replace(/{shop_name}/gi, page.name)
          .replace(/{school_name}/gi, page.school.name)}
        description={page.template.description}
      />

      <Collection
        products={page.products}
        featuredProductIds={page.template.featuredProductIds ?? []}
        paginationLimit={page.paginationLimit}
        collectionStrings={page.collectionStrings}
        filter={page.filter}
        sort={page.sort}
        printShop={page}
      />

      <Modules
        modules={page.template?.modules}
        collectionProducts={page.products}
      />
    </Layout>
  )
}

interface YearShopPageParams extends ParsedUrlQuery {
  id: string
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<YearShopPageParams>): Promise<
  GetStaticPropsResult<YearShopPageProps>
> {
  const yearShopPage = await getPrintShopPage(
    locale,
    params?.id ?? '',
    SanityPrintShopType.YEAR,
    getPreview(preview, previewData)
  )

  if (!yearShopPage.page) {
    return { notFound: true }
  }

  return {
    props: {
      locale,
      page: yearShopPage.page,
      site: yearShopPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<YearShopPageParams>
> {
  const allShops = await getAllPrintShopIds(SanityPrintShopType.YEAR)
  const allLocales = getAllLocales()
  const paths: StaticPathWithId[] = allShops
    .map((shop) => ({
      ...shop,
      locale: shop.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ id }) => !!id)
    .map(({ locale, id }) => ({
      params: {
        id,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default YearShopPage
